import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import {
	createResource,
	useResource,
	type RouteResource,
	type UseResourceHookResponse,
} from '@atlassian/react-resource-router';

/**
 * The tenant-context product slug for the Assets Vertical service.
 *
 * This is the slug used by the AGG `tenantContexts.activationIdByProduct`
 * resolver to return the Assets Vertical (AVS) activation/workspace id.
 * It is intentionally distinct from `jsm-cmdb` (the legacy CMDB workspace).
 */
export const AVS_PRODUCT_SLUG = 'assets-vertical';

type AvsWorkspaceQueryResponse = {
	readonly tenantContexts: ReadonlyArray<{
		readonly activationIdByProduct: {
			readonly active: string | null;
		} | null;
	} | null> | null;
} | null;

/** The resolved Assets Vertical workspace (activation) id, or null when unavailable. */
export type AvsWorkspaceResult = string | null;

type AvsWorkspaceQueryOperation = {
	readonly data?: AvsWorkspaceQueryResponse;
	readonly errors?: ReadonlyArray<{
		readonly message?: string;
	}>;
};

type AvsWorkspaceGraphQlRequest = {
	readonly query: string;
	readonly variables: {
		readonly cloudId: string;
		readonly product: string;
	};
};

export const RESOURCE_TYPE_ITAM_AVS_WORKSPACE = 'ITAM_AVS_WORKSPACE';

/**
 * Resolves the active activation (workspace) id for a given cloud id and the
 * Assets Vertical product slug.
 *
 * Mirrors the AVP pattern (`tenantContexts.activationIdByProduct`) so ITAM can
 * source the Assets Vertical workspace id rather than the legacy `jsm-cmdb`
 * CMDB workspace id.
 */
const avsWorkspaceQuery = `
	query itamAvsWorkspaceResourceQuery($cloudId: ID!, $product: String!) {
		tenantContexts(cloudIds: [$cloudId]) {
			activationIdByProduct(product: $product) {
				active
			}
		}
	}
`;

/**
 * Fetches the tenant's Assets Vertical workspace (activation) id via GraphQL.
 * Shared by the route resource (below) and any component that needs to load
 * this data on demand.
 */
export const fetchAvsWorkspaceId = async (cloudId: string): Promise<AvsWorkspaceResult> => {
	if (!cloudId) {
		throw new Error('Unable to fetch AVS workspace id: missing cloudId');
	}

	const requestBody: AvsWorkspaceGraphQlRequest = {
		query: avsWorkspaceQuery,
		variables: { cloudId, product: AVS_PRODUCT_SLUG },
	};

	const response = await performPostRequest<AvsWorkspaceQueryOperation>('/gateway/api/graphql', {
		body: JSON.stringify(requestBody),
		headers: {
			'content-type': 'application/json',
			'Atl-CloudId': cloudId,
		},
	});

	return response.data?.tenantContexts?.[0]?.activationIdByProduct?.active ?? null;
};

export const avsWorkspaceResource: RouteResource<AvsWorkspaceResult> =
	createResource<AvsWorkspaceResult>({
		type: RESOURCE_TYPE_ITAM_AVS_WORKSPACE,
		maxAge: Infinity,
		getKey: () => 'state',
		getData: async (_routerContext, { tenantContext }) => {
			const cloudId = tenantContext?.cloudId;
			if (!cloudId) {
				throw new Error('Unable to fetch AVS workspace id: missing tenantContext.cloudId');
			}

			return fetchAvsWorkspaceId(cloudId);
		},
	});

export function useAvsWorkspaceResource(): UseResourceHookResponse<AvsWorkspaceResult> {
	return useResource(avsWorkspaceResource);
}
